import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Typography,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { bankAPI } from '../../services/api';

const BankManagement = () => {
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bankName, setBankName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadBanks();
  }, []);

  const loadBanks = async () => {
    try {
      const response = await bankAPI.getAll();
      setBanks(response.data);
    } catch {
      setError('Failed to load banks');
    }
  };

  const openAdd = () => {
    setEditing(null);
    setBankName('');
    setError('');
    setOpen(true);
  };

  const openEdit = (bank) => {
    setEditing(bank);
    setBankName(bank.bank_name);
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setBankName('');
    setEditing(null);
    setError('');
  };

  const handleSubmit = async () => {
    if (!bankName.trim()) {
      setError('Bank name is required');
      return;
    }
    try {
      if (editing) {
        await bankAPI.update(editing.bank_id, { bank_name: bankName.trim() });
        setMessage('Bank updated successfully');
      } else {
        await bankAPI.create({ bank_name: bankName.trim() });
        setMessage('Bank added successfully');
      }
      handleClose();
      loadBanks();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (bank) => {
    if (!window.confirm(`Delete "${bank.bank_name}"? Employees assigned to this bank will lose the association.`)) return;
    try {
      await bankAPI.delete(bank.bank_id);
      setMessage('Bank deleted successfully');
      loadBanks();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete bank');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Typography variant="h6">Bank Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
          Add Bank
        </Button>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      {error && !open && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Bank Name</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No banks added yet. Click "Add Bank" to get started.
                </TableCell>
              </TableRow>
            ) : (
              banks.map((bank, idx) => (
                <TableRow key={bank.bank_id} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{bank.bank_name}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(bank)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(bank)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit Bank' : 'Add Bank'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            autoFocus
            label="Bank Name"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankManagement;
